/**
 * Gamepad device — standard mapping, §3.5 radial dead zones, §3.3 Focus Aim hysteresis,
 * §3.10 vibration table. Silent no-op when the browser exposes no actuator; on mobile it falls
 * back to `navigator.vibrate` behind its own gesture-unlock flag.
 */

import type { Vec2 } from '../sim/entities';
import { ACTIONS, BindingTable } from './bindings';

/**
 * §3.5 — radial dead zones, direction preserved, linear response, no expo.
 * `m' = clamp((m − inner) / (outer − inner), 0, 1)`. Returns `m'`.
 */
export function applyRadialDeadzone(x: number, y: number, inner: number, outer: number, out: Vec2): number {
  const m = Math.sqrt(x * x + y * y);
  if (m <= 0) {
    out.x = 0;
    out.y = 0;
    return 0;
  }
  let r = (m - inner) / (outer - inner);
  if (r <= 0) {
    out.x = 0;
    out.y = 0;
    return 0;
  }
  if (r > 1) r = 1;
  const k = r / m;
  out.x = x * k;
  out.y = y * k;
  return r;
}

/**
 * §3.3 — deliberate hysteresis on the gamepad Focus Aim latch: engage at ≥ engage deflection,
 * release below the release threshold. Measured on the raw stick magnitude.
 */
export function focusHysteresis(engaged: boolean, raw: number, engage: number, release: number): boolean {
  if (engaged) return raw >= release;
  return raw >= engage;
}

// --- §3.10 vibration table ---

export type VibrationEvent = 'player_hit' | 'boss_event' | 'elite_kill' | 'level_up' | 'low_hp' | 'dash';

export interface VibrationSpec {
  low: number;
  high: number;
  ms: number;
  pulses: number;
  /** Higher wins; `player_hit` preempts all (§3.10). */
  priority: number;
  /** WICG Web Haptics effect for this cue, when the UA has the API. */
  web: WebHapticEffect;
  webIntensity: number;
}

export const VIBRATION: Readonly<Record<VibrationEvent, VibrationSpec>> = {
  player_hit: { low: 0.9, high: 0.6, ms: 180, pulses: 1, priority: 100, web: 'edge', webIntensity: 1 },
  boss_event: { low: 0.7, high: 0.3, ms: 400, pulses: 1, priority: 80, web: 'edge', webIntensity: 0.8 },
  elite_kill: { low: 0.4, high: 0.5, ms: 120, pulses: 1, priority: 60, web: 'tick', webIntensity: 0.7 },
  level_up: { low: 0.3, high: 0.6, ms: 75, pulses: 2, priority: 50, web: 'align', webIntensity: 0.9 },
  low_hp: { low: 0.25, high: 0.0, ms: 120, pulses: 1, priority: 20, web: 'hint', webIntensity: 0.5 },
  dash: { low: 0.2, high: 0.4, ms: 90, pulses: 1, priority: 30, web: 'tick', webIntensity: 0.45 },
};

/**
 * WICG Web Haptics (github.com/WICG/web-haptics): `navigator.playHaptics(effect, intensity)`
 * over four named effects. It is an incubation proposal — no browser ships it today — so
 * every call is feature-detected and the §3.10 rumble/vibrate path below stays the
 * implementation that actually runs. When a UA does ship it, a phone gets the sharp,
 * device-tuned version of the same six cues for free.
 *
 * The mapping reads the spec's own definitions against what the event does TO the ship:
 *
 *  - `edge`, "a heavy boundary signal" — taking a hit, and a boss arriving or dying. Both
 *    are the run hitting a wall.
 *  - `tick`, "a firm pulse that marks discrete changes" — an elite dying, and the dash,
 *    which is one discrete shove of the hull.
 *  - `align`, "a crisp confirmation when an object locks into place" — the level-up, the
 *    one moment the ship becomes measurably more than it was.
 *  - `hint`, "a light, subtle cue" — the low-hull warning, which must never feel like the
 *    damage that caused it.
 */
export type WebHapticEffect = 'hint' | 'edge' | 'tick' | 'align';

interface HapticsCapableNavigator extends Navigator {
  playHaptics?: (effect: WebHapticEffect, intensity?: number) => void;
}

/** True when the UA exposes the proposal. Re-read per call: nothing caches a missing API. */
function playWebHaptics(effect: WebHapticEffect, intensity: number): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as HapticsCapableNavigator;
  if (typeof nav.playHaptics !== 'function') return false;
  try {
    nav.playHaptics(effect, Math.max(0, Math.min(1, intensity)));
    return true;
  } catch {
    // The proposal requires sticky user activation and returns undefined either way, so a
    // throw here means "not now", not "broken". The rumble path still runs.
    return false;
  }
}

/** §3.10 — low HP rumbles at most once per second. */
const LOW_HP_COOLDOWN_TICKS = 60;

interface RumbleParams {
  startDelay: number;
  duration: number;
  weakMagnitude: number;
  strongMagnitude: number;
}

interface RumbleActuator {
  playEffect(type: string, params: RumbleParams): Promise<unknown>;
  reset?(): Promise<unknown>;
}

const RUMBLE: RumbleParams = { startDelay: 0, duration: 0, weakMagnitude: 0, strongMagnitude: 0 };

function swallow(): void {
  /* actuator rejection is not an error worth surfacing */
}

// packed gamepad code kinds
const PAD_NONE = -1;
const PAD_BUTTON = 0;
const PAD_AXIS = 1;
const PAD_STICK = 2;

/** kind << 16 | index << 1 | negative-sign bit. */
function packPadCode(code: string): number {
  if (code === 'g:lstick') return (PAD_STICK << 16) | (0 << 1);
  if (code === 'g:rstick') return (PAD_STICK << 16) | (1 << 1);
  if (code.charCodeAt(0) !== 103 || code.charCodeAt(1) !== 58) return PAD_NONE;
  if (code.charCodeAt(2) === 98 /* b */) {
    const n = parseInt(code.slice(3), 10);
    return Number.isFinite(n) ? (PAD_BUTTON << 16) | (n << 1) : PAD_NONE;
  }
  if (code.charCodeAt(2) === 97 /* a */ && code.charCodeAt(3) === 120 /* x */) {
    const last = code.charCodeAt(code.length - 1);
    const neg = last === 45 /* - */ ? 1 : 0;
    const n = parseInt(code.slice(4, code.length - 1), 10);
    return Number.isFinite(n) ? (PAD_AXIS << 16) | (n << 1) | neg : PAD_NONE;
  }
  return PAD_NONE;
}

const AXIS_DIGITAL_THRESHOLD = 0.5;

export class GamepadDevice {
  connected = false;
  activity = false;
  heldMask = 0;
  pressedMask = 0;
  readonly pressTick = new Int32Array(ACTIONS.length).fill(-1);

  /** Post-dead-zone left stick (movement). */
  readonly move: Vec2 = { x: 0, y: 0 };
  moveMagnitude = 0;
  /** Post-dead-zone right stick (Focus Aim). */
  readonly aim: Vec2 = { x: 0, y: 0 };
  aimMagnitude = 0;
  /** §3.3 hysteresis latch: engage ≥ 0.40, release < 0.30. */
  focusEngaged = false;

  deadzoneInner = 0.18;
  deadzoneOuter = 0.92;
  focusEngage = 0.4;
  focusRelease = 0.3;

  /** Set false to keep the device silent (§13 accessibility toggle). */
  hapticsEnabled = true;
  /** navigator.vibrate needs a user gesture on iOS/Android; the manager flips this on first touch. */
  vibrateUnlocked = false;

  private tick = 0;
  private revision = -1;
  private padIndex = -1;
  private readonly slotCodes = new Int32Array(ACTIONS.length * 2).fill(PAD_NONE);
  private readonly prevButtons = new Uint8Array(32);
  private activePriority = 0;
  private activeUntilTick = -1;
  private pendingPulseTick = -1;
  private pendingLow = 0;
  private pendingHigh = 0;
  private pendingMs = 0;
  private lastLowHpTick = -1000;

  constructor(private bindings: BindingTable) {}

  setDeadzones(inner: number, outer: number): void {
    this.deadzoneInner = inner;
    this.deadzoneOuter = outer;
  }

  /** Polls the pad and rebuilds held/pressed masks. Call once per sim tick. */
  poll(tick: number): void {
    this.tick = tick;
    if (this.revision !== this.bindings.revision) {
      this.revision = this.bindings.revision;
      this.rebuildSlots();
    }
    const pad = this.pickPad();
    if (!pad) {
      if (this.connected) {
        this.connected = false;
        this.heldMask = 0;
        this.move.x = 0;
        this.move.y = 0;
        this.moveMagnitude = 0;
        this.aim.x = 0;
        this.aim.y = 0;
        this.aimMagnitude = 0;
        this.focusEngaged = false;
        this.prevButtons.fill(0);
      }
      return;
    }
    this.connected = true;
    this.readSticks(pad);
    this.readButtons(pad);
    this.servicePulse(pad);
  }

  drainPressed(): number {
    const m = this.pressedMask;
    this.pressedMask = 0;
    return m;
  }

  /** §3.10 raw rumble. Silent no-op without an actuator. */
  vibrate(low: number, high: number, durationMs: number): void {
    if (!this.hapticsEnabled || durationMs <= 0) return;
    const pad = this.pickPad();
    const act = pad ? (pad as unknown as { vibrationActuator?: RumbleActuator }).vibrationActuator : undefined;
    if (act && typeof act.playEffect === 'function') {
      RUMBLE.startDelay = 0;
      RUMBLE.duration = durationMs;
      RUMBLE.weakMagnitude = clamp01(high);
      RUMBLE.strongMagnitude = clamp01(low);
      try {
        const p = act.playEffect('dual-rumble', RUMBLE);
        if (p && typeof p.catch === 'function') p.catch(swallow);
      } catch {
        /* unsupported effect type */
      }
      return;
    }
    if (this.vibrateUnlocked && typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      try {
        navigator.vibrate(Math.round(durationMs));
      } catch {
        /* blocked by the UA */
      }
    }
  }

  /** §3.10 table lookup with preemption. `player_hit` always wins. */
  vibrateEvent(event: VibrationEvent): void {
    if (!this.hapticsEnabled) return;
    const spec = VIBRATION[event];
    if (!spec) return;
    if (event === 'low_hp') {
      if (this.tick - this.lastLowHpTick < LOW_HP_COOLDOWN_TICKS) return;
      this.lastLowHpTick = this.tick;
    }
    if (this.tick < this.activeUntilTick && spec.priority < this.activePriority) return;
    this.activePriority = spec.priority;
    this.activeUntilTick = this.tick + Math.ceil((spec.ms * spec.pulses * 2) / 16.666667);
    // The proposal's device-tuned effect first. It targets the screen/trackpad actuator, so
    // it is tried alongside — not instead of — a connected pad's rumble: a player holding a
    // controller on a haptic laptop should feel the pad, and `vibrate` picks that up below.
    const web = playWebHaptics(spec.web, spec.webIntensity);
    // `navigator.vibrate` is the crude version of the same cue. Firing both on one device
    // would double it, so the fallback is skipped once the sharp path has spoken and no pad
    // is present to answer instead.
    if (!web || this.padIndex >= 0) this.vibrate(spec.low, spec.high, spec.ms);
    if (spec.pulses > 1) {
      this.pendingPulseTick = this.tick + Math.ceil((spec.ms * 2) / 16.666667);
      this.pendingLow = spec.low;
      this.pendingHigh = spec.high;
      this.pendingMs = spec.ms;
    }
  }

  private servicePulse(_pad: Gamepad): void {
    if (this.pendingPulseTick >= 0 && this.tick >= this.pendingPulseTick) {
      this.pendingPulseTick = -1;
      this.vibrate(this.pendingLow, this.pendingHigh, this.pendingMs);
    }
  }

  private pickPad(): Gamepad | null {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null;
    const pads = navigator.getGamepads();
    if (!pads) return null;
    if (this.padIndex >= 0) {
      const p = pads[this.padIndex];
      if (p && p.connected) return p;
      this.padIndex = -1;
    }
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (p && p.connected) {
        this.padIndex = i;
        return p;
      }
    }
    return null;
  }

  private readSticks(pad: Gamepad): void {
    const ax = pad.axes;
    const lx = ax.length > 0 ? ax[0] : 0;
    const ly = ax.length > 1 ? ax[1] : 0;
    const rx = ax.length > 2 ? ax[2] : 0;
    const ry = ax.length > 3 ? ax[3] : 0;
    this.moveMagnitude = applyRadialDeadzone(lx, ly, this.deadzoneInner, this.deadzoneOuter, this.move);
    this.aimMagnitude = applyRadialDeadzone(rx, ry, this.deadzoneInner, this.deadzoneOuter, this.aim);
    // §3.3 hysteresis measured on the RAW deflection, not the renormalized magnitude.
    const rawAim = Math.sqrt(rx * rx + ry * ry);
    this.focusEngaged = focusHysteresis(this.focusEngaged, rawAim, this.focusEngage, this.focusRelease);
    if (this.moveMagnitude > 0 || rawAim >= this.focusEngage) this.activity = true;
  }

  private readButtons(pad: Gamepad): void {
    const btns = pad.buttons;
    const ax = pad.axes;
    let held = 0;
    for (let i = 0; i < ACTIONS.length; i++) {
      const base = i << 1;
      for (let s = 0; s < 2; s++) {
        const packed = this.slotCodes[base + s];
        if (packed === PAD_NONE) continue;
        const kind = packed >> 16;
        const index = (packed & 0xffff) >> 1;
        const neg = (packed & 1) === 1;
        let on = false;
        if (kind === PAD_BUTTON) {
          on = index < btns.length && btns[index].pressed;
        } else if (kind === PAD_AXIS) {
          const v = index < ax.length ? ax[index] : 0;
          on = neg ? v < -AXIS_DIGITAL_THRESHOLD : v > AXIS_DIGITAL_THRESHOLD;
        }
        if (on) {
          held |= 1 << i;
          break;
        }
      }
    }
    const edges = held & ~this.heldMask;
    if (edges !== 0) {
      this.activity = true;
      this.pressedMask |= edges;
      for (let i = 0; i < ACTIONS.length; i++) {
        if (edges & (1 << i)) this.pressTick[i] = this.tick;
      }
    }
    this.heldMask = held;
    for (let i = 0; i < btns.length && i < this.prevButtons.length; i++) {
      this.prevButtons[i] = btns[i].pressed ? 1 : 0;
    }
  }

  private rebuildSlots(): void {
    for (let i = 0; i < ACTIONS.length; i++) {
      const a = ACTIONS[i];
      this.slotCodes[i << 1] = packPadCode(this.bindings.get(a, 'gamepad', 'primary'));
      this.slotCodes[(i << 1) + 1] = packPadCode(this.bindings.get(a, 'gamepad', 'alt'));
    }
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
